import React from 'react';
import { Link } from 'react-router-dom';
import styled from 'styled-components';
import { motion } from 'framer-motion';

const PageContainer = styled.div`
  padding-top: 80px;
`;
const HeroSection = styled.section`
  background-color: var(--black);
  color: var(--white);
  padding: 6rem 0;
  text-align: center;
`;
const HeroContent = styled.div`
  max-width: 800px;
  margin: 0 auto;
  padding: 0 2rem;
`;
const PageTitle = styled.h1`
  font-size: 3rem;
  margin-bottom: 1.5rem;
  @media (max-width: 768px) { font-size: 2.5rem; }
`;
const PageDescription = styled.p`
  font-size: 1.2rem;
  color: var(--gray-300);
  margin-bottom: 2rem;
  line-height: 1.6;
  @media (max-width: 768px) { font-size: 1rem; }
`;
const UseCasesContainer = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 5rem 2rem;
`;
const UseCaseGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 3rem;
  @media (max-width: 992px) { grid-template-columns: 1fr; }
`;
const UseCaseCard = styled(motion.div)`
  background-color: var(--white);
  border-radius: 8px;
  overflow: hidden;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.05);
  border: 1px solid var(--gray-200);
  transition: all 0.3s ease;
  &:hover {
    box-shadow: 0 15px 40px rgba(0, 0, 0, 0.1);
    transform: translateY(-10px);
    border-color: var(--primary);
  }
`;
const UseCaseImage = styled.div`
  height: 250px;
  overflow: hidden;
  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    transition: transform 0.5s ease;
  }
  ${UseCaseCard}:hover & img {
    transform: scale(1.05);
  }
`;
const UseCaseContent = styled.div`
  padding: 2rem;
`;
const UseCaseCategory = styled.div`
  display: inline-block;
  background-color: var(--primary-light);
  color: var(--primary-dark);
  padding: 0.5rem 1rem;
  border-radius: 20px;
  font-size: 0.9rem;
  font-weight: 600;
  margin-bottom: 1rem;
`;
const UseCaseTitle = styled.h3`
  font-size: 1.8rem;
  margin-bottom: 1rem;
  color: var(--black);
`;
const UseCaseExcerpt = styled.p`
  color: var(--gray-700);
  margin-bottom: 2rem;
  line-height: 1.6;
`;
const UseCaseLink = styled(Link)`
  color: var(--primary);
  font-weight: 600;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  &:hover { gap: 0.75rem; }
`;

const useCases = [
  {
    slug: 'cloud-transformation',
    image: require('../assets/images/cloud.png'),
    category: 'Cloud Solutions',
    title: 'Cloud Transformation Strategy for Manufacturing',
    excerpt: 'How Platform0 could streamline complex operations for a global manufacturing enterprise.',
  },
  {
    slug: 'ai-pharma',
    image: require('../assets/images/aiconsultant.png'),
    category: 'AI Consultancy',
    title: 'AI Strategy for Pharma Documentation & Compliance',
    excerpt: 'Helping pharma organizations modernize documentation and compliance with AI.',
  },
  {
    slug: 'ai-food-beverage',
    image: require('../assets/images/transformation.png'),
    category: 'Digital Transformation',
    title: 'Automating Quality Control with AI in Food & Beverage',
    excerpt: 'Improving product consistency and reducing waste in food production.',
  },
  {
    slug: 'agri-food',
    image: require('../assets/images/aaas.jpg'),
    category: 'Cloud & Architecture',
    title: 'Digitization Roadmap for Agri-Food Certification',
    excerpt: 'Enabling efficiency and compliance in agriculture exports.',
  },
];

const UseCasesPage = () => {
  return (
    <PageContainer>
      <HeroSection>
        <HeroContent>
          <PageTitle>Use Cases</PageTitle>
          <PageDescription>
            Explore how Platform0 could help organizations leverage architecture, cloud, and AI to achieve business outcomes. These use cases are illustrative and hypothetical.
          </PageDescription>
        </HeroContent>
      </HeroSection>

      <UseCasesContainer>
        <UseCaseGrid>
          {useCases.map((uc, idx) => (
            <UseCaseCard
              key={uc.slug}
              whileHover={{ y: -10 }}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: idx * 0.1 }}
            >
              <UseCaseImage>
                <img src={uc.image} alt={uc.title} />
              </UseCaseImage>
              <UseCaseContent>
                <UseCaseCategory>{uc.category}</UseCaseCategory>
                <UseCaseTitle>{uc.title}</UseCaseTitle>
                <UseCaseExcerpt>{uc.excerpt}</UseCaseExcerpt>
                <UseCaseLink to={`/use-cases/${uc.slug}`}>
                  Read Use Case <span>&rarr;</span>
                </UseCaseLink>
              </UseCaseContent>
            </UseCaseCard>
          ))}
        </UseCaseGrid>
      </UseCasesContainer>
    </PageContainer>
  );
};

export default UseCasesPage;
